import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { MEASURE_DEFINITION_BY_TITLE } from '../analyticsDefinitions'
import { EXPLORER_TYPE } from '../renderTypes'
import { useCustomizationStore } from '~/stores/customization'
import { CustomizationField } from '~/types/customization'
import { getTableViewsFilters } from '~/services/explore'
import { OrderValue } from '~/types/cube'
import { DashboardModuleType, type DashboardConfig } from '~/types/dashboards'

export const createCareerDashboardConfig = (): DashboardConfig => {
  const customizationStore = useCustomizationStore()
  const employeeGroupDimension = customizationStore.getCustomizationByField(
    CustomizationField.EMPLOYEE_GROUP
  )

  return [
    {
      titleKey: 'dashboard.keyFigures',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.career.verticalMobilities.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_VERTICAL_MOBILITY,
                dimension: employeeGroupDimension,
                type: EXPLORER_TYPE.BAR
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_VERTICAL_MOBILITY]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.career.timeToPromote.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_TO_PROMOTE,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_TO_PROMOTE
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_TO_PROMOTE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.career.verticalMobilityRate.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_VERTICAL_MOBILITY_RATE,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_VERTICAL_MOBILITY_RATE
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_VERTICAL_MOBILITY_RATE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_VERTICAL_MOBILITY_RATE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.career.topStalledEmployees.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_TIME_WITHOUT_PROMOTION,
                DIMENSION_TITLES.EMPLOYEE_CAREER_LEVEL
              ],
              columnsTranslationPrefix:
                'dashboard.career.topStalledEmployees.columns',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_SINCE_PROMOTE,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_CAREER_LEVEL,
                DIMENSION_TITLES.EMPLOYEE_TIME_WITHOUT_PROMOTION
              ],
              filters: getTableViewsFilters(MEASURE_TITLES.EMPLOYEE_COUNT_END),
              order: [
                [
                  DIMENSION_TITLES.EMPLOYEE_TIME_WITHOUT_PROMOTION,
                  OrderValue.DESC
                ]
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.career.fastTrackEmployees.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_CURRENT_PROMOTION_TIME,
                DIMENSION_TITLES.EMPLOYEE_CAREER_LEVEL
              ],
              columnsTranslationPrefix:
                'dashboard.career.fastTrackEmployees.columns',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_VERTICAL_MOBILITY,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_VERTICAL_MOBILITY],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_CAREER_LEVEL,
                DIMENSION_TITLES.EMPLOYEE_CURRENT_PROMOTION_TIME
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.EMPLOYEE_COUNT_VERTICAL_MOBILITY
              ),
              order: [
                [
                  DIMENSION_TITLES.EMPLOYEE_CURRENT_PROMOTION_TIME,
                  OrderValue.ASC
                ]
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.DOUBLE,
          options: {
            config: {
              titleKey: 'dashboard.career.otherInternalMobilities.title',
              firstSubtitleKey:
                'dashboard.career.otherInternalMobilities.lateral.title',
              secondSubtitleKey:
                'dashboard.career.otherInternalMobilities.location.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_NON_VERTICAL_MOBILITY,
                type: EXPLORER_TYPE.LIST
              }
            },
            firstQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_LATERAL_MOBILITY]
            },
            secondQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_GEOGRAPHIC_MOBILITY]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.moreData',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.career.careerLevels.firstTitle',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END_CAREER_LEVEL,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END_CAREER_LEVEL]
            }
          }
        },
        {
          moduleType: DashboardModuleType.DOUBLE,
          options: {
            config: {
              titleKey: 'dashboard.career.managers.title',
              firstSubtitleKey: 'dashboard.career.managers.total.title',
              secondSubtitleKey: 'dashboard.career.managers.ratio.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END_MANAGER,
                type: EXPLORER_TYPE.LIST
              }
            },
            firstQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END_MANAGER]
            },
            secondQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_RATIO_MANAGER]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.career.avgTimeInRole.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_IN_ROLE,
                type: EXPLORER_TYPE.KPI
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_IN_ROLE]
            }
          }
        }
      ]
    }
  ]
}
